import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { useOutletContext, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  ChevronRight,
  ExternalLink,
  BarChart3,
  Calendar,
  FileText,
  CheckCircle2,
  Circle,
  PlayCircle,
  AlertTriangle,
  Clock,
  X,
  Loader2,
  TrendingUp
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get time-based greeting
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Goedemorgen';
  if (hour < 18) return 'Goedemiddag';
  return 'Goedenavond';
};

// Format date for berichten
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: '2-digit'
  });
};

// Bericht type config
const BERICHT_TYPE_CONFIG = {
  aankondiging: { label: 'Aankondiging', dotColor: 'bg-blue-500' },
  seizoensmelding: { label: 'Seizoensmelding', dotColor: 'bg-amber-500' },
  herinnering: { label: 'Herinnering', dotColor: 'bg-purple-500' },
  informatie: { label: 'Informatie', dotColor: 'bg-slate-400' }
};

// ── Performance Widget ───────────────────────────────────────────────────────

const STATUS_COLORS = {
  OK: { bg: 'border-emerald-200 bg-emerald-50', text: 'text-emerald-700' },
  BOVEN: { bg: 'border-amber-200 bg-amber-50', text: 'text-amber-700' },
  HOOG: { bg: 'border-red-200 bg-red-50', text: 'text-red-700' },
  KRITIEK: { bg: 'border-red-300 bg-red-100', text: 'text-red-800' },
};

const PerformanceWidget = ({ locatieId }) => {
  const navigate = useNavigate();
  const [perfData, setPerfData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getAccessToken();
    const params = locatieId ? `?locatie_id=${locatieId}` : '';
    fetch(`${API}/performance/mijn-locatie${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (r.status === 404 || r.status === 403) return null;
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then(d => setPerfData(d))
      .catch(err => { if (err.message) console.error('Performance widget error:', err.message); setPerfData(null); })
      .finally(() => setLoading(false));
  }, [locatieId]);

  if (loading || !perfData) return null;

  const h = perfData.huidig || perfData;
  const sc = STATUS_COLORS[h.status] || STATUS_COLORS.OK;
  const pkOk = (h.pk_pct || 0) <= (perfData.pk_target || 35);
  const bvOk = (h.bijverkoop_pct || 0) >= (perfData.bv_target || 7);

  return (
    <div
      className={`rounded-xl border-2 overflow-hidden ${sc.bg}`}
      data-testid="performance-widget"
    >
      <div className="px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-tw-blue" />
          <h2 className="font-semibold text-slate-800">Performance — {perfData.period_label}</h2>
        </div>
        <span className={`text-xs font-bold px-2 py-1 rounded-full ${sc.bg} ${sc.text}`}>
          {h.status}
        </span>
      </div>
      <div className="px-5 pb-4 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600">Omzet:</span>
          <span className="font-medium">
            {'\u20AC'}{(h.omzet || 0).toLocaleString('nl-NL')} / {'\u20AC'}{(h.target || 0).toLocaleString('nl-NL')} target
            <span className={`ml-2 font-bold ${(h.voortgang_pct || 0) >= 90 ? 'text-emerald-600' : (h.voortgang_pct || 0) >= 75 ? 'text-amber-600' : 'text-red-600'}`}>
              {h.voortgang_pct || 0}%
            </span>
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600">PK%:</span>
          <span className={`font-medium ${pkOk ? 'text-emerald-600' : 'text-red-600'}`}>
            {h.pk_pct || 0}% {pkOk ? '\u2714' : '\u2718'}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600">Bijverkoop:</span>
          <span className={`font-medium ${bvOk ? 'text-emerald-600' : 'text-red-600'}`}>
            {h.bijverkoop_pct || 0}% {bvOk ? '\u2714' : '\u2718'}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-2 text-tw-blue hover:bg-tw-blue/5"
          onClick={() => navigate('/performance')}
          data-testid="performance-widget-details-btn"
        >
          Bekijk volledig overzicht <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};


// ── Performance Detail Modal (bedrijfsleider read-only) ─────────────────────

const PerformanceDetailModal = ({ data, open, onClose }) => {
  const { canManage } = useAuth();
  const navigate = useNavigate();
  const [historie, setHistorie] = useState([]);

  useEffect(() => {
    if (!data || !open) return;
    const token = getAccessToken();
    fetch(`${API}/performance/locatie/${data.locatie}/historie`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setHistorie(d.historie || []))
      .catch(() => setHistorie([]));
  }, [data, open]);

  const chartData = historie.slice(-6).map(p => ({
    name: p.periode,
    Omzet: Math.round(p.omzet / 1000),
    Target: Math.round(p.target / 1000),
  }));

  if (!data) return null;
  const pkT = data.locatie === 'rotterdam' ? 33 : 35;
  const bvT = data.locatie === 'rotterdam' ? 8 : 7;
  const pkOk = data.pk_pct <= pkT;
  const bvOk = data.bijverkoop_pct >= bvT;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="performance-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-tw-blue" />
            {data.locatie_naam} — Performance
          </DialogTitle>
          <DialogDescription>{data.period_label}</DialogDescription>
        </DialogHeader>

        {/* KPI cards */}
        <div className="grid grid-cols-2 gap-3 my-4">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500">Omzet vs Target</p>
            <p className="text-lg font-bold">{'\u20AC'}{data.omzet.toLocaleString('nl-NL')}</p>
            <p className="text-xs text-slate-400">van {'\u20AC'}{data.target.toLocaleString('nl-NL')} ({data.voortgang_pct}%)</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500">YTD Positie</p>
            <p className={`text-lg font-bold ${(data.ytd?.achterstand || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {(data.ytd?.achterstand || 0) >= 0 ? '+' : ''}{'\u20AC'}{(data.ytd?.achterstand || 0).toLocaleString('nl-NL')}
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500">PK%</p>
            <p className={`text-lg font-bold ${pkOk ? 'text-emerald-600' : 'text-red-600'}`}>
              {data.pk_pct}% {pkOk ? '\u2714' : '\u2718'}
            </p>
            <p className="text-xs text-slate-400">Target: {pkT}%</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500">Bijverkoop%</p>
            <p className={`text-lg font-bold ${bvOk ? 'text-emerald-600' : 'text-red-600'}`}>
              {data.bijverkoop_pct}% {bvOk ? '\u2714' : '\u2718'}
            </p>
            <p className="text-xs text-slate-400">Target: {bvT}%</p>
          </div>
        </div>

        {/* Trend chart */}
        {chartData.length > 0 && (
          <div data-testid="performance-modal-chart">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Omzet trend (x1000)</h3>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="Omzet" stroke="#1f2548" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Target" stroke="#ffc315" strokeWidth={2} strokeDasharray="5 5" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <DialogFooter className="mt-4">
          {canManage() && (
            <Button
              className="bg-tw-blue hover:bg-tw-blue-light text-white"
              onClick={() => { onClose(); navigate('/management/performance'); }}
              data-testid="performance-modal-full-btn"
            >
              Volledige analyse
            </Button>
          )}
          <Button variant="outline" onClick={onClose} data-testid="performance-modal-close-btn">
            Sluiten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ── Taken Vandaag Section ───────────────────────────────────────────────────
const TakenVandaagSection = ({ taken, onStatusChange, onNavigate }) => {
  const items = taken?.items || [];
  const voltooid = taken?.voltooid || 0;
  const totaal = taken?.totaal || 0;

  const cycleStatus = (taak) => {
    const next = taak.status === 'open' ? 'bezig' : taak.status === 'bezig' ? 'klaar' : 'open';
    onStatusChange(taak._id, next);
  };

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 overflow-hidden"
      data-testid="taken-vandaag-section"
    >
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#1f2548]" />
          <h2 className="font-semibold text-slate-800">Vandaag</h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500" data-testid="taken-progress">
            {voltooid}/{totaal} klaar
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-blue-600 hover:text-blue-700 px-2 h-8"
            onClick={() => onNavigate('/werkprogramma')}
            data-testid="taken-meer-link"
          >
            Alle taken
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      <div className="divide-y divide-slate-50">
        {items.map((taak) => (
          <div
            key={taak._id}
            className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 group"
            data-testid={`taak-item-${taak._id}`}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <button
                onClick={() => cycleStatus(taak)}
                className="flex-shrink-0 focus:outline-none"
                data-testid={`taak-status-btn-${taak._id}`}
                title={`Status: ${taak.status}`}
              >
                {taak.status === 'klaar' ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : taak.status === 'bezig' ? (
                  <PlayCircle className="w-5 h-5 text-blue-500" />
                ) : (
                  <Circle className="w-5 h-5 text-slate-300 group-hover:text-slate-400" />
                )}
              </button>
              <span
                className={`truncate ${
                  taak.status === 'klaar'
                    ? 'text-slate-400 line-through'
                    : 'text-slate-700'
                }`}
              >
                {taak.titel}
              </span>
              {taak.isExtern && (
                <a
                  href={taak.externUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-4 h-4 text-slate-400 hover:text-blue-500" />
                </a>
              )}
            </div>
            {taak.tijd && (
              <span className="text-sm text-slate-400 flex-shrink-0 ml-3">
                {taak.tijd}
              </span>
            )}
          </div>
        ))}

        {items.length === 0 && (
          <div
            className="px-5 py-8 text-center text-slate-400"
            data-testid="taken-empty"
          >
            Geen taken voor vandaag
          </div>
        )}
      </div>
    </div>
  );
};

// ── Alerts Section ──────────────────────────────────────────────────────────
const AlertsSection = ({ alerts, onNavigate }) => {
  if (!alerts || alerts.length === 0) return null;

  const colorMap = {
    red: 'bg-red-500',
    orange: 'bg-orange-500',
    blue: 'bg-blue-500'
  };

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 overflow-hidden"
      data-testid="alerts-section"
    >
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-500" />
        <h2 className="font-semibold text-slate-800">Aandacht</h2>
        <span className="ml-auto bg-slate-100 text-slate-600 text-xs font-medium px-2 py-1 rounded-full">
          {alerts.length}
        </span>
      </div>

      <div className="divide-y divide-slate-50">
        {alerts.map((alert, idx) => (
          <button
            key={idx}
            onClick={() => {
              if (alert.link.startsWith('#')) return;
              onNavigate(alert.link);
            }}
            className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 text-left"
            data-testid={`alert-item-${alert.type}`}
          >
            <div className="flex items-center gap-3">
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  colorMap[alert.kleur] || 'bg-slate-400'
                }`}
              />
              <span className="text-slate-700">{alert.titel}</span>
            </div>
            {!alert.link.startsWith('#') && (
              <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Berichten Section ───────────────────────────────────────────────────────
const BerichtenSection = ({ berichten, onOpenBericht }) => {
  const ongelezen = berichten?.filter((b) => !b.gelezen).length || 0;

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 overflow-hidden"
      data-testid="berichten-section"
    >
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <svg
          className="w-5 h-5 text-slate-600"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
          />
        </svg>
        <h2 className="font-semibold text-slate-800">Berichten</h2>
        {ongelezen > 0 && (
          <span className="ml-auto bg-blue-100 text-blue-600 text-xs font-medium px-2 py-1 rounded-full">
            {ongelezen} nieuw
          </span>
        )}
      </div>

      <div className="divide-y divide-slate-50">
        {berichten?.map((bericht) => {
          const typeConfig =
            BERICHT_TYPE_CONFIG[bericht.type] || BERICHT_TYPE_CONFIG.informatie;
          return (
            <button
              key={bericht._id}
              className="w-full px-5 py-3 hover:bg-slate-50 text-left"
              onClick={() => onOpenBericht(bericht)}
              data-testid={`bericht-item-${bericht._id}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${typeConfig.dotColor}`}
                  />
                  <span
                    className={`font-medium truncate ${
                      bericht.gelezen ? 'text-slate-600' : 'text-slate-800'
                    }`}
                  >
                    {bericht.titel}
                  </span>
                  {!bericht.gelezen && (
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0" />
                  )}
                  {bericht.prioriteit === 'urgent' && (
                    <Badge className="bg-red-100 text-red-700 text-[10px] px-1.5 py-0">
                      Urgent
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-slate-400 flex-shrink-0 ml-2">
                  {formatDate(bericht.startDatum)}
                </span>
              </div>
              <p className="text-sm text-slate-500 line-clamp-1 pl-4">
                {bericht.inhoud}
              </p>
            </button>
          );
        })}

        {(!berichten || berichten.length === 0) && (
          <div
            className="px-5 py-6 text-center text-slate-400"
            data-testid="berichten-empty"
          >
            Geen nieuwe berichten
          </div>
        )}
      </div>
    </div>
  );
};

// ── Snelkoppelingen Section ─────────────────────────────────────────────────
const SnelkoppelingenSection = () => (
  <div data-testid="snelkoppelingen-section">
    <h2 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
      <ChevronRight className="w-4 h-4 text-slate-400" />
      Snel naar
    </h2>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <a
        href="https://daily-ops.nl"
        target="_blank"
        rel="noopener noreferrer"
        className="bg-white rounded-xl border border-slate-200 p-4 text-center hover:border-blue-300 hover:shadow-md transition-all"
        data-testid="quick-link-daily-ops"
      >
        <BarChart3 className="w-8 h-8 mx-auto mb-2 text-blue-600" />
        <span className="font-medium text-slate-700">Daily-Ops</span>
        <ExternalLink className="w-3 h-3 inline ml-1 text-slate-400" />
      </a>

      <a
        href="https://smart-roster.nl"
        target="_blank"
        rel="noopener noreferrer"
        className="bg-white rounded-xl border border-slate-200 p-4 text-center hover:border-green-300 hover:shadow-md transition-all"
        data-testid="quick-link-rooster"
      >
        <Calendar className="w-8 h-8 mx-auto mb-2 text-green-600" />
        <span className="font-medium text-slate-700">Rooster</span>
        <ExternalLink className="w-3 h-3 inline ml-1 text-slate-400" />
      </a>

      <Link
        to="/locatie-info"
        className="bg-white rounded-xl border border-slate-200 p-4 text-center hover:border-slate-300 hover:shadow-md transition-all"
        data-testid="quick-link-locatie-info"
      >
        <FileText className="w-8 h-8 mx-auto mb-2 text-slate-600" />
        <span className="font-medium text-slate-700">Locatie Info</span>
      </Link>
    </div>
  </div>
);

// ── Main Dashboard ──────────────────────────────────────────────────────────
const Overzicht = () => {
  const { selectedLocatie, selectedLocatieData } = useOutletContext();
  const navigate = useNavigate();
  const { isBedrijfsleider } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBericht, setSelectedBericht] = useState(null);
  const [perfModalData, setPerfModalData] = useState(null);

  // 4.1: `stil` verversen zonder paginabrede loader — anders flipt `loading` en
  // vuurt het genereer-effect opnieuw (oneindige POST/GET-lus op telefoons).
  const fetchDashboard = useCallback(async (stil = false) => {
    if (!selectedLocatie) { setLoading(false); return; }
    try {
      if (!stil) setLoading(true);
      const token = getAccessToken();
      const res = await fetch(`${API}/locaties/${selectedLocatie}/dashboard`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        setData(await res.json());
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      if (!stil) setLoading(false);
    }
  }, [selectedLocatie]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // 4.1: hooguit één keer per locatie per dag taken genereren
  const gegenereerd = useRef(new Set());
  useEffect(() => {
    if (loading || !data || data.taken?.totaal !== 0 || !selectedLocatie) return;
    const vandaag = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Amsterdam' });
    const sleutel = `${selectedLocatie}:${vandaag}`;
    if (gegenereerd.current.has(sleutel)) return;
    gegenereerd.current.add(sleutel);
    (async () => {
      try {
        const token = getAccessToken();
        await fetch(`${API}/locaties/${selectedLocatie}/taken/genereer?periode=vandaag`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        fetchDashboard(true);
      } catch (err) {
        console.error('Auto-generate tasks error:', err);
      }
    })();
  }, [loading, data, selectedLocatie, fetchDashboard]);

  const handleStatusChange = async (taakId, newStatus) => {
    try {
      const token = getAccessToken();
      const res = await fetch(
        `${API}/locaties/${selectedLocatie}/taken/${taakId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ status: newStatus })
        }
      );
      if (res.ok) {
        // Optimistic update
        setData((prev) => {
          if (!prev) return prev;
          const updatedItems = prev.taken.items.map((t) =>
            t._id === taakId ? { ...t, status: newStatus } : t
          );
          return {
            ...prev,
            taken: {
              items: updatedItems,
              voltooid: updatedItems.filter((t) => t.status === 'klaar').length,
              totaal: updatedItems.length
            }
          };
        });
        const label = newStatus === 'klaar' ? 'Klaar' : newStatus === 'bezig' ? 'Bezig' : 'Open';
        toast.success(`Taak status: ${label}`);
      }
    } catch (err) {
      console.error('Status update error:', err);
      toast.error('Kon status niet bijwerken');
    }
  };

  const handleOpenBericht = async (bericht) => {
    setSelectedBericht(bericht);
    if (!bericht.gelezen) {
      try {
        const token = getAccessToken();
        await fetch(
          `${API}/locaties/${selectedLocatie}/berichten/${bericht._id}/gelezen`,
          {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );
        // Update local state
        setData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            berichten: prev.berichten.map((b) =>
              b._id === bericht._id ? { ...b, gelezen: true } : b
            )
          };
        });
      } catch (err) {
        console.error('Mark gelezen error:', err);
      }
    }
  };

  const handleNavigate = (path) => {
    if (path.startsWith('/')) {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1f2548] mx-auto mb-2" />
          <p className="text-slate-500">Laden...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12" data-testid="dashboard-empty">
        <p className="text-slate-500">Geen locatie geselecteerd</p>
      </div>
    );
  }

  return (
    <div
      className="space-y-6 max-w-4xl mx-auto px-4 sm:px-0"
      data-testid="dashboard-container"
    >
      {/* Header */}
      <div data-testid="dashboard-header">
        <h1 className="text-2xl font-bold text-[#1f2548]">
          {data.locatie.naam}
        </h1>
        <p className="text-slate-500">
          {getGreeting()}! Dit staat vandaag op de planning.
        </p>
      </div>

      {/* Taken Vandaag */}
      <TakenVandaagSection
        taken={data.taken}
        onStatusChange={handleStatusChange}
        onNavigate={handleNavigate}
      />

      {/* Performance Widget (bedrijfsleider en hoger) */}
      {isBedrijfsleider() && (
        <PerformanceWidget locatieId={selectedLocatie} />
      )}

      {/* Alerts */}
      <AlertsSection alerts={data.alerts} onNavigate={handleNavigate} />

      {/* Berichten */}
      <BerichtenSection
        berichten={data.berichten}
        onOpenBericht={handleOpenBericht}
      />

      {/* Snelkoppelingen */}
      <SnelkoppelingenSection />

      {/* Bericht Detail Dialog */}
      <Dialog
        open={!!selectedBericht}
        onOpenChange={() => setSelectedBericht(null)}
      >
        <DialogContent className="max-w-lg">
          {selectedBericht && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge
                    className={`text-xs ${
                      selectedBericht.prioriteit === 'urgent'
                        ? 'bg-red-100 text-red-700'
                        : selectedBericht.prioriteit === 'hoog'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {selectedBericht.prioriteit === 'urgent'
                      ? 'Urgent'
                      : selectedBericht.prioriteit === 'hoog'
                        ? 'Hoog'
                        : 'Normaal'}
                  </Badge>
                  <Badge className="bg-slate-100 text-slate-600 text-xs">
                    {BERICHT_TYPE_CONFIG[selectedBericht.type]?.label ||
                      'Informatie'}
                  </Badge>
                </div>
                <DialogTitle>{selectedBericht.titel}</DialogTitle>
                <DialogDescription>
                  {formatDate(selectedBericht.startDatum)} -{' '}
                  {formatDate(selectedBericht.eindDatum)}
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <p className="text-slate-700 whitespace-pre-wrap">
                  {selectedBericht.inhoud}
                </p>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setSelectedBericht(null)}
                  data-testid="bericht-dialog-close"
                >
                  Sluiten
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Performance Detail Modal */}
      <PerformanceDetailModal
        data={perfModalData}
        open={!!perfModalData}
        onClose={() => setPerfModalData(null)}
      />
    </div>
  );
};

export default Overzicht;
